import { createContext, useContext } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'

export interface DescriptionListProps extends HTMLAttributes<HTMLDListElement> {
  /** Layout: stacked label-over-value, two-column grid, or row. */
  layout?: 'stack' | 'grid' | 'row'
  /** Grid columns when layout="grid". */
  columns?: 1 | 2 | 3
  children: ReactNode
  /** Render on ink chrome. */
  ink?: boolean
}

export interface DescriptionItemProps {
  label: ReactNode
  children?: ReactNode
  /** Render value in font-mono. */
  mono?: boolean
  className?: string
  /** Render on ink chrome. Inherits from DescriptionList when omitted. */
  ink?: boolean
}

const gridColumns: Record<NonNullable<DescriptionListProps['columns']>, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
}

const DescriptionListInkContext = createContext(false)

export function DescriptionList({
  layout = 'stack',
  columns = 2,
  ink = false,
  className,
  children,
  ...props
}: DescriptionListProps) {
  return (
    <DescriptionListInkContext.Provider value={ink}>
      <dl
        className={cn(
          'm-0',
          layout === 'grid' && cn('grid gap-4', gridColumns[columns]),
          layout === 'stack' && 'grid gap-3',
          layout === 'row' && 'grid gap-3',
          ink ? 'text-on-ink' : 'text-ink-body',
          className,
        )}
        {...props}
      >
        {children}
      </dl>
    </DescriptionListInkContext.Provider>
  )
}

export function DescriptionItem({ label, children, mono = false, className, ink }: DescriptionItemProps) {
  const inheritedInk = useContext(DescriptionListInkContext)
  const resolvedInk = ink ?? inheritedInk
  const empty = isEmptyDescriptionValue(children)

  return (
    <div className={cn('min-w-0', className)}>
      <dt className={cn('text-micro font-semibold uppercase tracking-eyebrow', resolvedInk ? 'text-on-ink-faint' : 'text-ink-muted')}>
        {label}
      </dt>
      <dd className={cn('m-0 mt-1 min-w-0 break-words text-sm', mono && 'font-mono', resolvedInk ? 'text-on-ink' : 'text-ink-body')}>
        {empty ? <span aria-label="No value">&mdash;</span> : children}
      </dd>
    </div>
  )
}

export const Field = DescriptionItem

function isEmptyDescriptionValue(value: ReactNode): boolean {
  return value === null || value === undefined || value === false || (typeof value === 'string' && value.trim() === '')
}
